<script setup lang="ts">
import { onMounted, onUnmounted, ref, type PropType } from 'vue';
import Dialog from 'primevue/dialog';
import ProgressSpinner from 'primevue/progressspinner';
import type { EntryContext } from '@/models';
import { useEntryStore } from '@/stores/entry';
import Entry from './Entry.vue';

/**
 * Hosts the form entry over whatever view opened it, so the user keeps their place in that view. Closing is always the
 * host's job (it owns the URL the dialog is derived from) — this component only reports the intent, which lets the
 * router's unsaved-changes guard have the final say.
 */
defineProps({
  context: {
    type: Object as PropType<EntryContext>,
    required: true
  }
});

const emit = defineEmits(['onClose', 'onSaved']);

const entryStore = useEntryStore();

/** Reported by the entry's side rail. The dialog widens to make room for it, so the form column keeps its width whether
 * the rail is absent, collapsed to its tabs, or open. */
const railState = ref<'hidden' | 'collapsed' | 'open'>('hidden');

/** The virtualized field list measures itself on mount, so it must not mount while the dialog is still animating in. */
const contentVisible = ref(false);

const previousDocumentTitle = ref('');

onMounted(() => {
  previousDocumentTitle.value = document.title;
});

onUnmounted(() => {
  /** The route guard's entry-close telemetry is path-based and never fires for the query-only change that closes this
   * dialog, so report the close here — it covers cancel, save and browser-back alike. */
  entryStore.sendEntryCloseEvent();
  entryStore.clearEntryContext();
  document.title = previousDocumentTitle.value;
});
</script>

<template>
  <Dialog :visible="true"
    modal
    :class="['entry-dialog', `entry-dialog-rail-${railState}`]"
    :draggable="false"
    :showHeader="false"
    :breakpoints="{ '600px': '100vw' }"
    @show="contentVisible = true"
    @update:visible="emit('onClose')">
    <Entry v-if="contentVisible"
      :context="context"
      dialogMode
      @onClose="emit('onClose')"
      @onSaved="emit('onSaved')"
      @onRailState="railState = $event"/>
    <div class="entry-dialog-loader" v-else>
      <ProgressSpinner strokeWidth="4" />
    </div>
  </Dialog>
</template>

<style lang="scss">
/** Unscoped because the dialog is appended to the body, outside this component's style scope. */
.p-dialog.entry-dialog {
  /** One source of truth for the three widths the dialog is built from; the rail consumes the same two custom
   * properties (see EntryDialogRail.vue) so the dialog grows by exactly what the rail occupies. */
  --entry-form-width: 1180px;
  --entry-rail-tabs-width: 52px;
  --entry-rail-panel-width: 360px;

  width: min(var(--entry-form-width), 96vw);
  height: 92vh;
  max-height: 92vh;
  // Growing/shrinking as the rail opens should read as the panel sliding out, not the dialog jumping.
  transition: width 0.2s ease;
}
/** The rail is additive: each state widens the dialog rather than taking width away from the form. `96vw` still caps it,
 * so on a screen with no room left to grow the form gives up space as a last resort. */
.p-dialog.entry-dialog.entry-dialog-rail-collapsed {
  width: min(calc(var(--entry-form-width) + var(--entry-rail-tabs-width)), 96vw);
}
.p-dialog.entry-dialog.entry-dialog-rail-open {
  width: min(
    calc(var(--entry-form-width) + var(--entry-rail-tabs-width) + var(--entry-rail-panel-width)),
    96vw
  );
}
.p-dialog.entry-dialog .p-dialog-content {
  /** PrimeVue gives `.p-dialog-content` `flex-grow: 1` only when the dialog is maximized, so without this it collapses
   * to its natural height inside the dialog's flex column — leaving the entry's `height: 100%` chain nothing to resolve
   * against, and the rest of the dialog empty. `min-height: 0` then lets the virtualized list scroll rather than push
   * the box taller. */
  flex: 1 1 auto;
  min-height: 0;
  padding: 0;
  display: flex;
  align-items: stretch;
  // Anchors the side rail when it covers the form instead of sitting beside it (narrow screens).
  position: relative;
  // The entry scrolls internally; this box must not become a second scroller.
  overflow: hidden;
  border-radius: 6px;
}

@media (max-width: 600px) {
  .p-dialog.entry-dialog {
    height: 100vh;
    max-height: 100vh;
    margin: 0;
    border-radius: 0;
  }
}
</style>

<style scoped lang="scss">
.entry-dialog-loader {
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
}
</style>
